import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Container,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  CircularProgress,
  Backdrop,
  Snackbar,
  TextField,
  Typography,
  Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import Alert from 'src/components/Alert';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ListAltIcon from '@material-ui/icons/ListAlt';
import ReceiptIcon from '@material-ui/icons/Receipt';
import { useDispatch, useSelector } from 'react-redux';
import { getPartnerReports, savePartnerReportGuid } from 'src/store/slices/partnerReportSlice';
import { getUser } from 'src/utils/Common';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  },
  container: {
    maxHeight: 630
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  reportBox: {
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme.spacing(3),
    background: 'linear-gradient(135deg, #e3f2fd 0%, #fce4ec 100%)',
    boxShadow: '0 8px 24px 0 rgba(60,72,88,0.10)',
    cursor: 'pointer',
    transition: 'box-shadow 0.3s, transform 0.2s',
    position: 'relative',
    overflow: 'hidden',
    border: 'none',
    '&:hover': {
      boxShadow: '0 12px 32px 0 rgba(60,72,88,0.18)',
      transform: 'translateY(-4px) scale(1.03)',
      background: 'linear-gradient(135deg, #bbdefb 0%, #f8bbd0 100%)'
    },
    padding: theme.spacing(4),
    margin: theme.spacing(1, 0)
  },
  iconWrapper: {
    background: 'linear-gradient(135deg, #1976d2 0%, #d81b60 100%)',
    borderRadius: '50%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: '0 2px 8px 0 rgba(60,72,88,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  reportTitle: {
    fontWeight: 700,
    fontSize: 22,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.dark,
    textAlign: 'center'
  },
  reportDesc: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontSize: 16
  }
}));

const staticReports = [
  {
    key: 'partnerSummary',
    title: 'Partner Summary Report',
    description: 'View summary of partner activities and totals.',
    icon: <AssessmentIcon style={{ fontSize: 40, color: '#fff' }} />
  },
  {
    key: 'partnerTransactions',
    title: 'Partner Transactions',
    description: 'Detailed list of all partner transactions.',
    icon: <ListAltIcon style={{ fontSize: 40, color: '#fff' }} />
  },
  {
    key: 'partnerInvoices',
    title: 'Partner Invoices',
    description: 'Access invoice reports for partners.',
    icon: <ReceiptIcon style={{ fontSize: 40, color: '#fff' }} />
  }
  // Add more report definitions as needed
];

function PartnerReport() {
  const classes = useStyles();
  const { t } = useTranslation('overview');
  const dispatch = useDispatch();
  const partnerReportState = useSelector(state => state.partnerReport || {});
  const partnerReports = partnerReportState.reports || [];
  const loading = partnerReportState.loading || false;
  const [uyari, setUyari] = useState(false);
  const [uyariTip, setUyariTip] = useState('default');
  const [responseMessage, setResponseMessage] = useState({
    ErrorCode: '0',
    ErrorDescription: t('success_message')
  });
  const SessionUser = getUser();

  useEffect(() => {
    dispatch(getPartnerReports());
    // eslint-disable-next-line
  }, [dispatch]);

  // Use fetched reports if available, otherwise fallback to static
  const reports = partnerReports.length > 0 ? 
    partnerReports.map(report => ({
        Id: report.Id,
        title: report.Name,
        description: report.Description,
        link: report.Link,
        icon: <ListAltIcon style={{ fontSize: 40, color: '#fff' }}></ListAltIcon>
    }))
    : staticReports;

  const uyariKapat = (event, reason) => {
    if (reason === 'clickaway') return;
    setUyari(false);
  };

  const handleReportClick = (Id) => {
    const report = reports.find(r => r.Id === Id);
    if (report) {

      const dto = {
        PartnerReportId: Id,
        EmployeeId: SessionUser.TechnicianId,
        ServiceShopId: SessionUser.ServiceShopId,
        TenantId: SessionUser.TenantId
      }
      
      dispatch(savePartnerReportGuid(dto))
        .then(response => {
          setResponseMessage({
            ErrorCode: '0',
            ErrorDescription: t('Report saved successfully')
          });
          setUyariTip('success');
          setUyari(true);
          console.log(response)
          console.log(report)
          window.open(report.link + 'Guid=' + response?.payload?.ResponseObject?.Guid, '_blank');
        })
        .catch(error => {
          setResponseMessage({
            ErrorCode: '1',
            ErrorDescription: error.message || t('An error occurred while saving the report')
          });
          setUyariTip('error');
          setUyari(true);
        });
    }
  };

  return (
    <Page title={t('Partner Report')}>
      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar open={uyari} autoHideDuration={10000} onClose={uyariKapat}>
        <Alert
          onClose={uyariKapat}
          message={responseMessage.ErrorDescription}
          variant={uyariTip}
        />
      </Snackbar>
      <Container maxWidth="xl" className={classes.root}>
        <Grid container spacing={4} justifyContent="center">
          {reports.map(report => (
            <Grid item xs={12} sm={6} md={4} key={report.Id}>
              <Box
                className={classes.reportBox}
                onClick={() => handleReportClick(report.Id)}
                boxShadow={3}
              >
                <div className={classes.iconWrapper}>
                  {report.icon}
                </div>
                <Typography className={classes.reportTitle}>
                  {t(report.title)}
                </Typography>
                <Typography className={classes.reportDesc}>
                  {t(report.description)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Page>
  );
}

export default PartnerReport;
